import React from "react";
import { useNavigate } from "react-router-dom";

const Documentation = () => {
  const navigate = useNavigate();

  const documentationSections = [
    {
      title: "Getting Started",
      items: [
        { name: "Introduction", path: "/docs/intro" },
        { name: "Quick Start Guide", path: "/docs/quickstart" },
        { name: "Installation", path: "/docs/installation" },
        { name: "First dApp", path: "/docs/first-dapp" }
      ]
    },
    {
      title: "Core Concepts",
      items: [
        { name: "Architecture", path: "/docs/architecture" },
        { name: "Green Consensus", path: "/docs/consensus" },
        { name: "Tokenomics", path: "/docs/tokenomics" },
        { name: "Sustainability", path: "/docs/sustainability" }
      ]
    },
    {
      title: "API Reference",
      items: [
        { name: "REST API", path: "/docs/rest-api" },
        { name: "WebSocket API", path: "/docs/websocket-api" },
        { name: "JavaScript SDK", path: "/docs/js-sdk" },
        { name: "Smart Contracts", path: "/docs/smart-contracts" }
      ]
    },
    {
      title: "Tutorials",
      items: [
        { name: "Deploy a Contract", path: "/docs/deploy-contract" },
        { name: "Create an NFT", path: "/docs/create-nft" },
        { name: "Build a DEX", path: "/docs/build-dex" },
        { name: "Gas Optimization", path: "/docs/gas-optimization" }
      ]
    }
  ];

  const quickLinks = [
    { icon: "🚀", title: "API Playground", description: "Test our APIs in real-time" },
    { icon: "💬", title: "Community Discord", description: "Get help from our community" },
    { icon: "📚", title: "Video Tutorials", description: "Watch step-by-step guides" },
    { icon: "🐛", title: "Report Issues", description: "Help us improve the platform" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-eco-black to-eco-dark py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-eco-green hover:text-eco-blue mb-6 transition-colors"
          >
            ← Back to Ecosystem
          </button>
          <h1 className="text-4xl md:text-6xl font-black gradient-text mb-4">
            Documentation
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive guides, API references, and tutorials to help you build on X EcoChain.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              placeholder="Search documentation..."
              className="w-full px-6 py-4 bg-eco-dark border border-eco-green border-opacity-30 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-eco-green focus:ring-2 focus:ring-eco-green focus:ring-opacity-50"
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              ⌘K
            </div>
          </div>
        </div>

        {/* Documentation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {documentationSections.map((section, index) => (
            <div
              key={index}
              className="glass-card p-6 rounded-2xl border border-eco-green border-opacity-20 hover:border-eco-green hover:border-opacity-40 transition-all duration-300"
            >
              <h3 className="text-xl font-black text-white mb-4">{section.title}</h3>
              <ul className="space-y-3">
                {section.items.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    <button
                      onClick={() => navigate(item.path)}
                      className="text-eco-blue hover:text-eco-green transition-colors text-left w-full flex items-center group"
                    >
                      <span className="w-2 h-2 bg-eco-blue rounded-full mr-3 group-hover:bg-eco-green transition-colors"></span>
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Quick Links */}
        <div className="glass-card rounded-3xl p-8 border border-eco-green border-opacity-20 mb-12">
          <h2 className="text-2xl font-black text-white mb-8 text-center">Quick Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-2xl border border-gray-700 hover:border-eco-green hover:border-opacity-40 transition-all duration-300 group cursor-pointer"
                onClick={() => console.log(`Navigate to: ${link.title}`)}
              >
                <div className="text-3xl mb-3 group-hover:scale-110 transition-transform duration-300">
                  {link.icon}
                </div>
                <h4 className="font-bold text-white mb-2">{link.title}</h4>
                <p className="text-sm text-gray-400">{link.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Getting Started CTA */}
        <div className="text-center">
          <div className="glass-card p-8 rounded-3xl border border-eco-green border-opacity-30 max-w-4xl mx-auto">
            <h3 className="text-2xl font-black gradient-text mb-4">
              Ready to Dive Deeper?
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Join our developer community and start building the next generation of sustainable Web3 applications.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="px-8 py-3 bg-eco-green text-eco-black font-bold rounded-xl hover:scale-105 transition-transform duration-300"
                onClick={() => navigate("/developers")}
              >
                Visit Developer Portal
              </button>
              <button 
                className="px-8 py-3 bg-eco-blue text-eco-black font-bold rounded-xl hover:scale-105 transition-transform duration-300"
                onClick={() => window.open('https://discord.gg/xecochain', '_blank')}
              >
                Join Discord
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;